import type { CSSProperties } from 'react';
import { renderInlineMarkup } from '@source-components-react/shared/inline-markup';
import {
  LIST_INLINE_STYLES_ATTRIBUTE,
  LIST_LINKS_ATTRIBUTE,
  resolveListItems,
  resolveListKind,
  resolveListMarker,
  resolveListReversed,
  resolveListStart,
} from './list-attributes';

/**
 * One `<ul>` or `<ol>` with an `<li>` per authored line — and never a wrapper
 * around it.
 *
 * The element is the whole accessibility story. A real list element carries
 * the implicit `list` role, announces "list of N items" in a screen reader
 * and reads as structure to a search engine; styled divs would look the same
 * and say nothing. Flat lists only — nesting multiplies both the content
 * model and the editor for a structure most content does not need.
 *
 * Appearance is the component's marker choice with column styles on top: the
 * marker is spread first so anything the author set in Column Styles lands
 * later in the object and wins. Everything else — spacing, colour,
 * typography — is column styles on the root, inherited by the items, which
 * is why this component declares no other style attributes of its own.
 *
 * `{n|linked text}` markers inside any item resolve against one shared link
 * list, numbered by entry position exactly as in `r-text`. Anchors land
 * inside the `<li>`, never around it, and the same items accept the mark and
 * line-break markers `r-text` does.
 *
 * Nothing here needs the browser. The element, its items and its numbering
 * are all in the server's HTML, with no resolution after hydration.
 */
export function RList({
  componentAttributes,
  styleProps,
  className,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  /** Column styles from the widget pipeline. Always applied to the list element itself. */
  styleProps?: { [key: string]: any };
  /** Host-app classes for this column. The single root element carries them. */
  className?: string;
}>) {
  const marker = resolveListMarker(componentAttributes);
  const links = componentAttributes?.[LIST_LINKS_ATTRIBUTE];
  const inlineStyles = componentAttributes?.[LIST_INLINE_STYLES_ATTRIBUTE];

  const style: CSSProperties = {
    ...(marker !== undefined ? { listStyleType: marker } : {}),
    ...styleProps,
  };

  const items = resolveListItems(componentAttributes).map((item, position) => (
    <li key={position}>{renderInlineMarkup(item, links, inlineStyles)}</li>
  ));

  const sharedProps = {
    style,
    ...(className !== undefined ? { className } : {}),
  };

  if (resolveListKind(componentAttributes) === 'ordered') {
    const start = resolveListStart(componentAttributes);
    const reversed = resolveListReversed(componentAttributes);
    return (
      <ol
        {...sharedProps}
        {...(start !== undefined ? { start } : {})}
        {...(reversed ? { reversed: true } : {})}
      >
        {items}
      </ol>
    );
  }

  return <ul {...sharedProps}>{items}</ul>;
}
